import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import ModuleHeader from "../../moduleHeader";
import { getCurrentPackageUserModule, getLatestHumanChart } from "../../../services/api";

const PLANET_SYMBOLS = {
    Sun: "☉",
    Earth: "⊕",
    Moon: "☽",
    North_Node: "☊",
    South_Node: "☋",
    Mercury: "☿",
    Venus: "♀",
    Mars: "♂",
    Jupiter: "♃",
    Saturn: "♄",
    Uranus: "♅",
    Neptune: "♆",
    Pluto: "♇"
};

const ActivationItem = ({ activation, side }) => (
    <div className={`flex items-center gap-3 py-1.5 px-3 rounded-xl hover:bg-gray-50 transition-colors ${side === 'right' ? 'flex-row-reverse text-right' : ''}`}>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-lg ${side === 'left' ? 'bg-rose-50 text-rose-500' : 'bg-cyan-50 text-cyan-500'}`}>
            {PLANET_SYMBOLS[activation.planet]}
        </div>
        <div className="flex flex-col">
            <span className="text-[10px] font-black text-gray-400 uppercase leading-none mb-1">{activation.planet.replace('_', ' ')}</span>
            <span className="text-sm font-black text-gray-800 leading-none">{activation.gate}.{activation.line}</span>
        </div>
    </div>
);

const HumanDesignMap = ({ chartData: propChartData, packageData: propPackageData, embedded = false }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(!propChartData || !propPackageData);
    const [packageData, setPackageData] = useState(propPackageData || null);
    const [chartData, setChartData] = useState(propChartData || null);
    const [selectedCenter, setSelectedCenter] = useState(null);
    const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
    const [activeTab, setActiveTab] = useState("harita");

    const TABS = [
        { id: "ozet", label: "Özet" },
        { id: "harita", label: "Harita" },
        { id: "detay", label: "Analiz" }
    ];

    useEffect(() => {
        if (propChartData && propPackageData) {
            setChartData(propChartData);
            setPackageData(propPackageData);
            setLoading(false);
            return;
        }

        const initData = async () => {
            try {
                const pkg = await getCurrentPackageUserModule(id, 13);
                setPackageData(pkg);
                const latest = await getLatestHumanChart();
                if (latest && latest.success && latest.data) {
                    setChartData(latest.data);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        initData();
    }, [id, propChartData, propPackageData]);

    const handleTabClick = (tabId) => {
        if (tabId === "ozet") {
            navigate(`/${id}/13`);
        } else if (tabId === "detay") {
            navigate(`/${id}/13/analysis`);
        } else {
            setActiveTab(tabId);
        }
    };

    if (loading || !packageData) {
        return (
            <div className="w-full h-screen min-h-screen flex items-center justify-center bg-[#e8e9ed]">
                <div className="flex flex-col items-center justify-center">
                    <div className="relative mb-8">
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-24 w-24 border-4 border-transparent border-t-[#3DA7B5] border-r-[#3DA7B5] opacity-75"></div>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-16 w-16 border-[3px] border-transparent border-b-[#2a8a96] border-l-[#2a8a96] opacity-60" style={{ animationDirection: "reverse", animationDuration: "1.5s" }}></div>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#3DA7B5] via-[#2a8a96] to-[#3DA7B5] animate-pulse shadow-2xl"></div>
                        </div>
                    </div>
                    <p className="text-xl font-semibold text-gray-800 animate-pulse">Yükleniyor...</p>
                </div>
            </div>
        );
    }

    const blueprint = chartData?.energy_blueprint;

    // PASTEL PALETTE FOR CENTERS
    const COLORS = {
        HEAD: '#F9F0A0',
        AJNA: '#99D5CA',
        THROAT: '#C0A08C',
        G: '#F9F0A0',
        EGO: '#F2A6A6',
        SACRAL: '#F2A6A6',
        SPLEEN: '#C0A08C',
        SOLAR: '#C0A08C',
        ROOT: '#C0A08C',
        UNDEFINED: '#FFFFFF',
        DESIGN: '#DC2626',
        PERSONALITY: '#1F2937',
    };

    // Helper functions for channel rendering
    const getGateStatus = (gateNum) => ({
        isP: blueprint?.personality_activations?.some(a => a.gate === gateNum),
        isD: blueprint?.design_activations?.some(a => a.gate === gateNum)
    });

    const splitPathGeom = (p1, p2, ctrl, isCurved) => {
        if (!isCurved) {
            const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
            return [
                { s: p1, e: mid, isCurved: false },
                { s: mid, e: p2, isCurved: false }
            ];
        } else {
            const m1 = { x: (p1.x + ctrl.x) / 2, y: (p1.y + ctrl.y) / 2 };
            const m2 = { x: (ctrl.x + p2.x) / 2, y: (ctrl.y + p2.y) / 2 };
            const m3 = { x: (m1.x + m2.x) / 2, y: (m1.y + m2.y) / 2 };
            return [
                { s: p1, e: m3, c: m1, isCurved: true },
                { s: m3, e: p2, c: m2, isCurved: true }
            ];
        }
    };

    const renderSegment = (gateNum, geom, normal, keyPrefix) => {
        const { isP, isD } = getGateStatus(gateNum);
        if (!isP && !isD) return null;

        const { s, e, c, isCurved } = geom;

        const getPathD = (off) => {
            const start = { x: s.x + normal.x * off, y: s.y + normal.y * off };
            const end = { x: e.x + normal.x * off, y: e.y + normal.y * off };
            if (!isCurved) return `M ${start.x} ${start.y} L ${end.x} ${end.y}`;
            const ctrl = { x: c.x + normal.x * off, y: c.y + normal.y * off };
            return `M ${start.x} ${start.y} Q ${ctrl.x} ${ctrl.y} ${end.x} ${end.y}`;
        };

        const flowTransition = {
            duration: 3,
            repeat: Infinity,
            ease: "linear"
        };

        if (isP && isD) {
            return (
                <g key={keyPrefix}>
                    <motion.path
                        d={getPathD(-5)}
                        fill="none"
                        stroke={COLORS.PERSONALITY}
                        strokeWidth="10"
                        strokeLinecap="round"
                        initial={{ strokeDasharray: "10, 15", strokeDashoffset: 0 }}
                        animate={{ strokeDashoffset: -100 }}
                        transition={flowTransition}
                    />
                    <motion.path
                        d={getPathD(5)}
                        fill="none"
                        stroke={COLORS.DESIGN}
                        strokeWidth="10"
                        strokeLinecap="round"
                        initial={{ strokeDasharray: "10, 15", strokeDashoffset: 0 }}
                        animate={{ strokeDashoffset: -100 }}
                        transition={flowTransition}
                    />
                </g>
            );
        } else {
            return (
                <motion.path
                    key={keyPrefix}
                    d={getPathD(0)}
                    fill="none"
                    stroke={isP ? COLORS.PERSONALITY : COLORS.DESIGN}
                    strokeWidth="20"
                    strokeLinecap="round"
                    initial={{ strokeDasharray: "30, 20", strokeDashoffset: 0 }}
                    animate={{ strokeDashoffset: -200 }}
                    transition={flowTransition}
                />
            );
        }
    };

    // SVG PATHS
    const SVG_PATHS = {
        FIGURE: "M671.25,720.333c65.375-9.208,139.079-31.648,174.117-78.966c16.881-22.798,28.908-71.182,24.447-81.59c-5.669-14.687-12.07-18.102-41.12-12.717c-15.263,2.493-45.825-2.943-46.283-29.05c1.443-21.161,3.351-77.706-2.598-86.628c-5.948-8.922-25.356-11.331-23.87-21.74C757.432,399.234,787.812,340.841,788,329c0.342-11.055-10.485-25.269-14.176-29.8c-0.701-0.861-1.308-2.242-1.308-2.242s-0.447-1.587-0.411-2.653c0.353-10.534,3.855-69.289,39.709-123.306c47.271-71.216,126.082-72.703,163-72.703c31.227,0,114.516-5.073,179,72.703c87.732,105.736,22,227.669-18,297c-40.653,70.464-34.601,135.015-5.182,173.367c51.63,67.306,140.211,75.574,177.034,79.299c6.29,0.775,30.712,2.604,42,10.667c14,10,19.035,27.878,21,32.333c33.599,81.192,250.128,602.245,311.976,760.714c9.01,24.849,14.156,42.028,13.288,54.655c-1.088,15.818-10.16,31.84-21.706,44.259c-255.071,280.326-426.6,344.511-686.225,344.511l-0.22,0.005c-258.299,0-438.955-66.053-685.481-344.145c-10.958-12.36-20.065-29.441-20.555-45.333c-0.033-1.062-2.509-18.369,9.915-48.615c65.989-163.213,263.609-628.923,322.634-766.192c10.524-24.476,24.68-31.761,24.832-31.859C645.625,727.5,653.981,723.124,671.25,720.333z",
        HEAD: "M1122.594,193.576c19.709,30.77,3.73,55.714-35.688,55.715l-80.596,0.004c-9.854,0-25.832,0-35.687,0l-80.596-0.004c-39.418-0.001-55.396-24.945-35.688-55.715l40.295-62.911c4.927-7.692,12.915-20.163,17.843-27.855l40.301-62.907c19.711-30.769,51.668-30.769,71.379,0l40.3,62.907c4.928,7.692,12.917,20.163,17.844,27.855L1122.594,193.576z",
        AJNA: "M854.339,356.152c-19.708-30.77-3.729-55.714,35.688-55.715l80.596-0.003c9.854-0.001,25.832-0.001,35.687,0l80.596,0.003c39.419,0.001,55.397,24.945,35.688,55.715l-40.294,62.911c-4.928,7.692-12.916,20.163-17.844,27.855l-40.301,62.908c-19.711,30.768-51.667,30.768-71.378,0l-40.301-62.908c-4.928-7.692-12.916-20.163-17.844-27.855L854.339,356.152z",
        THROAT: "M1105.542,807.801c-0.001,29.746-22.378,53.859-49.982,53.86l-47.721,0.001c-11.042,0.001-28.943,0.001-39.984,0l-47.721-0.001c-27.604-0.001-49.981-24.114-49.982-53.86l-0.001-51.422c-0.001-11.898-0.001-31.188,0-43.087l0.001-51.423c0.001-29.745,22.378-53.858,49.982-53.859l47.721-0.001c11.041-0.001,28.942-0.001,39.984,0l47.721,0.001c27.604,0.001,49.981,24.114,49.982,53.859l0.001,51.423c0.001,11.898,0.001,31.188,0,43.087L1105.542,807.801z",
        G: "M1023.042,1203.557c-19.519,19.518-51.165,19.517-70.685-0.002l-33.744-33.743c-7.808-7.808-20.466-20.466-28.273-28.273l-33.742-33.745c-19.519-19.521-19.519-51.167-0.001-70.686l33.742-33.744c7.807-7.807,20.465-20.465,28.272-28.272l33.744-33.741c19.519-19.519,51.164-19.518,70.684,0.001l33.745,33.743c7.808,7.808,20.466,20.466,28.272,28.274l33.743,33.745c19.519,19.52,19.519,51.166,0.001,70.685l-33.742,33.744c-7.807,7.808-20.465,20.466-28.272,28.272L1023.042,1203.557z",
        EGO: "M1364.791,1286.496c11.264,24.11-3.228,39.686-32.368,34.789l-60.875-10.229c-7.377-1.239-19.338-3.25-26.715-4.49l-61.13-10.276c-29.14-4.898-38.271-24.444-20.396-43.657l37.34-40.135c4.525-4.864,11.862-12.75,16.389-17.613l37.5-40.302c17.877-19.211,41.501-15.241,52.767,8.868l23.534,50.365c2.852,6.104,7.476,15.999,10.327,22.104L1364.791,1286.496z",
        SACRAL: "M1105.542,1585.162c-0.001,27.972-22.378,50.647-49.982,50.648l-47.721,0.001c-11.042,0.001-28.943,0.001-39.984,0l-47.721-0.001c-27.604-0.001-49.981-22.677-49.982-50.648l-0.001-48.356c-0.001-11.189-0.001-29.329,0-40.518l0.001-48.356c0.001-27.973,22.378-50.648,49.982-50.648l47.721-0.002c11.041,0,28.942,0,39.984,0l47.721,0.002c27.604,0,49.981,22.676,49.982,50.648l0.001,48.356c0.001,11.188,0.001,29.328,0,40.518L1105.542,1585.162z",
        ROOT: "M1105.542,1903.162c-0.001,27.972-22.378,50.647-49.982,50.648l-47.721,0.001c-11.042,0.001-28.943,0.001-39.984,0l-47.721-0.001c-27.604-0.001-49.981-22.677-49.982-50.648l-0.001-48.356c-0.001-11.189-0.001-29.329,0-40.518l0.001-48.356c0.001-27.973,22.378-50.648,49.982-50.648l47.721-0.002c11.041,0,28.942,0,39.984,0l47.721,0.002c27.604,0,49.981,22.676,49.982,50.648l0.001,48.356c0.001,11.188,0.001,29.328,0,40.518L1105.542,1903.162z",
        SPLEEN: "M466.328,1362.758c-30.77-19.708-55.713-3.729-55.715,35.689l-0.003,80.596c0,9.854,0,25.832,0,35.687l0.003,80.595c0.001,39.419,24.945,55.397,55.715,35.689l62.911-40.295c7.692-4.927,20.164-12.915,27.855-17.843l62.908-40.301c30.768-19.711,30.768-51.668,0-71.379l-62.908-40.3c-7.692-4.928-20.163-12.917-27.855-17.844L466.328,1362.758z",
        SOLAR: "M1509.455,1362.758c30.77-19.708,55.714-3.729,55.715,35.689l0.003,80.596c0.001,9.854,0.001,25.832,0,35.687l-0.003,80.595c-0.001,39.419-24.945,55.397-55.715,35.689l-62.911-40.295c-7.692-4.927-20.163-12.915-27.855-17.843l-62.908-40.301c-30.768-19.711-30.768-51.668,0-71.379l62.908-40.3c7.692-4.928,20.163-12.917,27.855-17.844L1509.455,1362.758z"
    };

    // CHANNEL PATHS
    const CHANNEL_PATHS = {
        '64-47': 'M 990 250 L 990 300',
        '61-24': 'M 1006 250 L 1006 300',
        '63-4': 'M 1022 250 L 1022 300',
        '17-62': 'M 930 480 L 930 608',
        '43-23': 'M 1006 510 L 1006 608',
        '11-56': 'M 1080 480 L 1080 608',
        '16-48': 'M 870 735 Q 650 900 550 1328',
        '31-7': 'M 960 862 L 960 1020',
        '8-1': 'M 1006 862 L 1006 1020',
        '33-13': 'M 1052 862 L 1052 1020',
        '35-36': 'M 1106 735 Q 1350 900 1520 1328',
        '12-22': 'M 1090 770 Q 1300 900 1510 1400',
        '45-21': 'M 1095 790 Q 1150 900 1200 1050',
        '10-34': 'M 900 1130 Q 880 1300 920 1446',
        '20-57': 'M 900 1130 Q 700 1250 490 1400',
        '25-51': 'M 1112 1100 L 1200 1200',
        '7-31': 'M 970 1070 L 970 862',
        '15-5': 'M 970 1150 L 970 1446',
        '2-14': 'M 1006 1185 L 1006 1446',
        '46-29': 'M 1042 1150 L 1042 1446',
        '51-25': 'M 1230 1210 L 1132 1100',
        '40-37': 'M 1320 1280 Q 1400 1350 1470 1500',
        '26-44': 'M 1230 1270 Q 900 1400 530 1520',
        '21-45': 'M 1290 1230 Q 1200 1050 1095 790',
        '59-6': 'M 1072 1530 Q 1250 1480 1410 1480',
        '27-50': 'M 970 1580 Q 750 1520 560 1440',
        '5-15': 'M 970 1480 L 970 1150',
        '14-2': 'M 1006 1480 L 1006 1185',
        '29-46': 'M 1042 1480 L 1042 1150',
        '42-53': 'M 970 1610 L 970 1765',
        '3-60': 'M 1006 1580 L 1006 1765',
        '9-52': 'M 1042 1610 L 1042 1765',
        '54-32': 'M 940 1780 Q 750 1650 590 1480',
        '38-28': 'M 955 1910 Q 700 1750 560 1560',
        '58-18': 'M 1006 1910 Q 750 1750 490 1560',
        '39-55': 'M 1057 1840 Q 1300 1700 1510 1560',
        '41-30': 'M 1057 1910 Q 1250 1750 1440 1560',
        '19-49': 'M 1057 1860 Q 1280 1650 1440 1440',
        '53-42': 'M 970 1765 L 970 1610',
        '52-9': 'M 1042 1765 L 1042 1610',
    };

    // GATE COORDINATES - positioned inside centers based on reference image
    const GATE_COORDS = {
        // HEAD CENTER (top triangle) - numbers below the triangle
        64: { x: 920, y: 210 }, 61: { x: 990, y: 210 }, 63: { x: 1050, y: 210 },

        // AJNA CENTER (inverted triangle)
        // Top row inside Ajna - horizontal
        47: { x: 920, y: 340 }, 24: { x: 990, y: 340 }, 4: { x: 1060, y: 340 },
        // Middle row - corners of triangle
        17: { x: 935, y: 430 }, 11: { x: 1045, y: 430 },
        // Bottom tip of triangle
        43: { x: 990, y: 495 },

        // THROAT CENTER (brown rectangle)
        // Top row - inside rectangle
        62: { x: 920, y: 647 }, 23: { x: 990, y: 647 }, 56: { x: 1060, y: 647 },
        // Second row - sides
        16: { x: 900, y: 710 }, 35: { x: 1080, y: 700 },
        // Third row - sides
        20: { x: 900, y: 757 }, 12: { x: 1080, y: 747 },
        // Fourth row - right only
        45: { x: 1080, y: 797 },
        // Bottom row - inside rectangle
        31: { x: 920, y: 847 }, 8: { x: 990, y: 847 }, 33: { x: 1060, y: 847 },

        // G CENTER (diamond) - Identity
        // Top center
        1: { x: 990, y: 950 },
        // Upper row
        7: { x: 920, y: 1010 }, 13: { x: 1060, y: 1010 },
        // Middle/widest row - outer corners
        10: { x: 850, y: 1080 }, 25: { x: 1130, y: 1080 },
        // Lower row
        15: { x: 920, y: 1150 }, 46: { x: 1060, y: 1150 },
        // Bottom center
        2: { x: 990, y: 1210 },

        // EGO CENTER (triangle right side)
        // Top
        21: { x: 1280, y: 1150 },
        // Middle
        51: { x: 1220, y: 1210 },
        // Bottom left
        26: { x: 1170, y: 1270 },
        // Bottom right
        40: { x: 1350, y: 1310 },

        // SACRAL CENTER (red/pink rectangle - upper)
        // Top row
        5: { x: 920, y: 1425 }, 14: { x: 990, y: 1425 }, 29: { x: 1060, y: 1425 },
        // Second row - left only
        34: { x: 900, y: 1475 },
        // Third row - sides
        27: { x: 900, y: 1535 }, 59: { x: 1080, y: 1535 },
        // Bottom row
        42: { x: 920, y: 1595 }, 3: { x: 990, y: 1595 }, 9: { x: 1060, y: 1595 },

        // ROOT CENTER (brown/dark rectangle - lower)
        // Top row
        53: { x: 920, y: 1750 }, 60: { x: 990, y: 1750 }, 52: { x: 1060, y: 1750 },
        // Second row - sides
        54: { x: 900, y: 1810 }, 19: { x: 1080, y: 1810 },
        // Third row - sides
        38: { x: 900, y: 1870 }, 39: { x: 1080, y: 1870 },
        // Bottom row - sides
        58: { x: 900, y: 1930 }, 41: { x: 1080, y: 1930 },

        // SPLEEN CENTER (triangle left side - pointing right)
        // Top left - aligned with 18
        48: { x: 430, y: 1360 },
        // Top middle - aligned with 28
        57: { x: 480, y: 1400 },
        // Right side (with circle) - aligned with 32
        44: { x: 540, y: 1440 },
        // Below 44
        50: { x: 620, y: 1500 },
        // Middle center
        32: { x: 540, y: 1560 },
        // Lower left
        28: { x: 480, y: 1600 },
        // Bottom left
        18: { x: 430, y: 1630 },

        // SOLAR PLEXUS CENTER (triangle pointing left - mirror of Spleen)
        // Top right - aligned with 30
        36: { x: 1540, y: 1360 },
        // Top area - aligned with 55
        22: { x: 1480, y: 1400 },
        // Left side - aligned with 49
        37: { x: 1420, y: 1440 },
        // Below 37
        6: { x: 1340, y: 1500 },
        // Middle center
        49: { x: 1420, y: 1540 },
        // Lower right
        55: { x: 1480, y: 1580 },
        // Bottom right (with circle)
        30: { x: 1540, y: 1620 }
    };

    const isDefined = (centerName) => {
        if (centerName === 'Solar') {
            return blueprint?.centers?.defined?.includes('Solar') ||
                blueprint?.centers?.defined?.includes('Solar Plexus') ||
                blueprint?.centers?.defined?.includes('SolarPlexus');
        }
        return blueprint?.centers?.defined?.includes(centerName);
    };

    const CENTER_GATES = {
        Head: [64, 61, 63],
        Ajna: [47, 24, 4, 17, 43, 11],
        Throat: [62, 23, 56, 16, 20, 35, 12, 31, 8, 33, 45],
        G: [1, 7, 13, 10, 25, 15, 46, 2],
        Ego: [51, 21, 26, 40],
        Sacral: [5, 14, 29, 34, 59, 27, 42, 3, 9],
        Root: [53, 60, 52, 54, 19, 38, 58, 39, 41],
        Spleen: [48, 57, 50, 44, 32, 28, 18],
        Solar: [36, 22, 49, 37, 6, 55, 30]
    };

    const CENTER_NAMES_TR = {
        Head: 'Kafa Merkezi',
        Ajna: 'Ajna Merkezi',
        Throat: 'Boğaz Merkezi',
        G: 'G Merkezi (Kimlik)',
        Ego: 'Kalp/Ego Merkezi',
        Sacral: 'Sakral Merkezi',
        Root: 'Kök Merkezi',
        Spleen: 'Dalak Merkezi',
        Solar: 'Solar Pleksus'
    };

    const content = (
        <div className={embedded ? "w-full" : "min-h-screen bg-white w-full px-6 py-4"}>
            {!embedded && (
                <div className="bg-gray-100/60 p-1 rounded-full flex items-center relative gap-1 mb-6">
                    {TABS.map((tab) => {
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => handleTabClick(tab.id)}
                                className={`relative flex-1 py-2.5 rounded-full text-[10px] font-black transition-all duration-500 ${isActive ? "text-white" : "text-gray-400"}`}
                            >
                                {isActive && (
                                    <motion.div
                                        layoutId="hdTabMap"
                                        className="absolute inset-0 bg-[#3DA7B5] rounded-full shadow-lg shadow-[#3DA7B5]/20"
                                        transition={{ type: "spring", duration: 0.6 }}
                                    />
                                )}
                                <span className="relative z-10">{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
            >
                {/* Bodygraph SVG */}
                <div className="relative w-full max-w-xl mx-auto aspect-square overflow-visible">
                    <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 2000 2000" className="w-full h-full drop-shadow-sm">
                        <g id="figure_layer">
                            <path d={SVG_PATHS.FIGURE} fill="#F0F0F0" style={{ mixBlendMode: 'multiply' }} opacity="0.8" />
                        </g>

                        <g id="centers_layer">
                            <path d={SVG_PATHS.HEAD} fill={isDefined('Head') ? COLORS.HEAD : COLORS.UNDEFINED} stroke={COLORS.UNDEFINED} strokeWidth="5" onClick={() => setSelectedCenter('Head')} style={{ cursor: 'pointer' }} />
                            <path d={SVG_PATHS.AJNA} fill={isDefined('Ajna') ? COLORS.AJNA : COLORS.UNDEFINED} stroke={COLORS.UNDEFINED} strokeWidth="5" onClick={() => setSelectedCenter('Ajna')} style={{ cursor: 'pointer' }} />
                            <path d={SVG_PATHS.THROAT} fill={isDefined('Throat') ? COLORS.THROAT : COLORS.UNDEFINED} stroke={COLORS.UNDEFINED} strokeWidth="5" onClick={() => setSelectedCenter('Throat')} style={{ cursor: 'pointer' }} />
                            <path d={SVG_PATHS.G} fill={isDefined('G') ? COLORS.G : COLORS.UNDEFINED} stroke={COLORS.UNDEFINED} strokeWidth="5" onClick={() => setSelectedCenter('G')} style={{ cursor: 'pointer' }} />
                            <path d={SVG_PATHS.EGO} fill={isDefined('Ego') ? COLORS.EGO : COLORS.UNDEFINED} stroke={COLORS.UNDEFINED} strokeWidth="5" onClick={() => setSelectedCenter('Ego')} style={{ cursor: 'pointer' }} />
                            <path d={SVG_PATHS.SPLEEN} fill={isDefined('Spleen') ? COLORS.SPLEEN : COLORS.UNDEFINED} stroke={COLORS.UNDEFINED} strokeWidth="5" onClick={() => setSelectedCenter('Spleen')} style={{ cursor: 'pointer' }} />
                            <path d={SVG_PATHS.SOLAR} fill={isDefined('Solar') ? COLORS.SOLAR : COLORS.UNDEFINED} stroke={COLORS.UNDEFINED} strokeWidth="5" onClick={() => setSelectedCenter('Solar')} style={{ cursor: 'pointer' }} />
                            <path d={SVG_PATHS.SACRAL} fill={isDefined('Sacral') ? COLORS.SACRAL : COLORS.UNDEFINED} stroke={COLORS.UNDEFINED} strokeWidth="5" onClick={() => setSelectedCenter('Sacral')} style={{ cursor: 'pointer' }} />
                            <path d={SVG_PATHS.ROOT} fill={isDefined('Root') ? COLORS.ROOT : COLORS.UNDEFINED} stroke={COLORS.UNDEFINED} strokeWidth="5" onClick={() => setSelectedCenter('Root')} style={{ cursor: 'pointer' }} />
                        </g>

                        {/* Channels Layer - Connection lines between centers */}
                        <g id="channels_layer">
                            {Object.entries(CHANNEL_PATHS).map(([channelId, pathDStr]) => {
                                const [gate1, gate2] = channelId.split('-').map(Number);
                                const coord1 = GATE_COORDS[gate1];
                                const coord2 = GATE_COORDS[gate2];
                                if (!coord1 || !coord2) return null;

                                // Shorten the line from both ends so gate numbers are visible
                                const dx = coord2.x - coord1.x;
                                const dy = coord2.y - coord1.y;
                                const length = Math.sqrt(dx * dx + dy * dy);
                                const offsetVal = 40;
                                const ux = dx / length;
                                const uy = dy / length;

                                let pStart = { x: coord1.x + ux * offsetVal, y: coord1.y + uy * offsetVal };
                                let pEnd = { x: coord2.x - ux * offsetVal, y: coord2.y - uy * offsetVal };
                                let pCtrl = null;
                                let isCurved = false;

                                // Special curve logic (keep coordinates as established)
                                const is1034 = (gate1 === 10 && gate2 === 34) || (gate1 === 34 && gate2 === 10);
                                const is1648 = (gate1 === 16 && gate2 === 48) || (gate1 === 48 && gate2 === 16);
                                const is2057 = (gate1 === 20 && gate2 === 57) || (gate1 === 57 && gate2 === 20);
                                const is3536 = (gate1 === 35 && gate2 === 36) || (gate1 === 36 && gate2 === 35);
                                const is1222 = (gate1 === 12 && gate2 === 22) || (gate1 === 22 && gate2 === 12);

                                if (is1034 || is1648 || is2057 || is3536 || is1222) {
                                    isCurved = true;
                                    const gStart = (gate1 === 10 || gate1 === 16 || gate1 === 20 || gate1 === 35 || gate1 === 12) ? coord1 : coord2;
                                    const gEnd = (gate1 === 34 || gate1 === 48 || gate1 === 57 || gate1 === 36 || gate1 === 22) ? coord1 : coord2;

                                    if (is1034) {
                                        pStart = { x: gStart.x - 35, y: gStart.y };
                                        pEnd = { x: gEnd.x - 35, y: gEnd.y };
                                        pCtrl = { x: Math.min(pStart.x, pEnd.x) - 300, y: (pStart.y + pEnd.y) / 2 };
                                    } else if (is1648) {
                                        pStart = { x: gStart.x - 35, y: gStart.y };
                                        pEnd = { x: gEnd.x, y: gEnd.y - 35 };
                                        pCtrl = { x: Math.min(pStart.x, pEnd.x) - 5, y: (pStart.y + pEnd.y) / 2 };
                                    } else if (is2057) {
                                        pStart = { x: gStart.x - 35, y: gStart.y };
                                        pEnd = { x: gEnd.x, y: gEnd.y - 35 };
                                        pCtrl = { x: Math.min(pStart.x, pEnd.x) - 20, y: (pStart.y + pEnd.y) / 2 };
                                    } else if (is3536) {
                                        pStart = { x: gStart.x + 35, y: gStart.y };
                                        pEnd = { x: gEnd.x, y: gEnd.y - 35 };
                                        const midX = (pStart.x + pEnd.x) / 2;
                                        const midY = (pStart.y + pEnd.y) / 2;
                                        pCtrl = { x: midX + 130, y: midY - 65 };
                                    } else if (is1222) {
                                        pStart = { x: gStart.x + 35, y: gStart.y };
                                        pEnd = { x: gEnd.x, y: gEnd.y - 35 };
                                        const midX = (pStart.x + pEnd.x) / 2;
                                        const midY = (pStart.y + pEnd.y) / 2;
                                        pCtrl = { x: Math.max(pStart.x, pEnd.x) + 5, y: midY };
                                    }
                                    // Handle gate order for pStart/pEnd consistency
                                    if (gate1 !== (is1034 ? 10 : is1648 ? 16 : is2057 ? 20 : is3536 ? 35 : 12)) {
                                        const tmp = pStart; pStart = pEnd; pEnd = tmp;
                                    }
                                }

                                // Calculate Normal for offsets (from start to end)
                                const fullDx = pEnd.x - pStart.x;
                                const fullDy = pEnd.y - pStart.y;
                                const fullLen = Math.sqrt(fullDx * fullDx + fullDy * fullDy);
                                const normal = { x: -fullDy / fullLen, y: fullDx / fullLen };

                                const fullPathD = isCurved ? `M ${pStart.x} ${pStart.y} Q ${pCtrl.x} ${pCtrl.y} ${pEnd.x} ${pEnd.y}` : `M ${pStart.x} ${pStart.y} L ${pEnd.x} ${pEnd.y}`;
                                const segments = splitPathGeom(pStart, pEnd, pCtrl, isCurved);

                                return (
                                    <g key={channelId}>
                                        {/* Background track - Now white so it's visible against the figure */}
                                        <path d={fullPathD} fill="none" stroke="#FFFFFF" strokeWidth="20" strokeLinecap="round" />
                                        {/* Gate 1 Half */}
                                        {renderSegment(gate1, segments[0], normal, `${channelId}-g1`)}
                                        {/* Gate 2 Half */}
                                        {renderSegment(gate2, segments[1], normal, `${channelId}-g2`)}
                                    </g>
                                );
                            })}
                        </g>

                        {/* Gates Layer */}
                        <g id="gates_layer">
                            {Object.entries(GATE_COORDS).map(([gate, { x, y }]) => {
                                const gateNum = parseInt(gate);
                                const isPersonality = blueprint?.personality_activations?.some(a => a.gate === gateNum);
                                const isDesign = blueprint?.design_activations?.some(a => a.gate === gateNum);
                                const isActive = isPersonality || isDesign;

                                let circleFill = 'transparent';
                                let circleStroke = 'transparent';
                                let strokeWidth = 0;
                                let textColor = '#8B7355'; // Brown/gray for inactive gates

                                if (isActive) {
                                    circleFill = '#FFFFFF'; // White background
                                    textColor = '#1F2937'; // Dark text on white background
                                    strokeWidth = 4;

                                    // Design activation = red stroke
                                    if (isDesign && !isPersonality) {
                                        circleStroke = '#B91C1C'; // Red stroke for design
                                    }
                                    // Personality activation = black stroke
                                    else if (isPersonality && !isDesign) {
                                        circleStroke = '#1F2937'; // Black stroke for personality
                                    }
                                    // Both = half and half or use darker stroke
                                    else if (isPersonality && isDesign) {
                                        circleStroke = '#1F2937'; // Use personality stroke when both
                                    }
                                }

                                return (
                                    <g key={gate}>
                                        {isActive && (
                                            <circle
                                                cx={x}
                                                cy={y}
                                                r="30"
                                                fill={circleFill}
                                                stroke={circleStroke}
                                                strokeWidth={strokeWidth}
                                            />
                                        )}
                                        <text x={x} y={y + 10} textAnchor="middle" fontSize="28" fontWeight="600" fill={textColor} style={{ pointerEvents: 'none' }}>
                                            {gate}
                                        </text>
                                    </g>
                                );
                            })}
                        </g>
                    </svg>
                </div>

                {/* Center Detail Popup */}
                {selectedCenter && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setSelectedCenter(null)}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-white rounded-[2rem] p-6 max-w-sm w-full shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl" style={{ backgroundColor: isDefined(selectedCenter) ? COLORS[selectedCenter.toUpperCase()] || COLORS.UNDEFINED : COLORS.UNDEFINED }} />
                                    <div>
                                        <h3 className="text-lg font-black text-gray-800">{CENTER_NAMES_TR[selectedCenter]}</h3>
                                        <span className={`text-xs font-bold ${isDefined(selectedCenter) ? 'text-emerald-500' : 'text-gray-400'}`}>
                                            {isDefined(selectedCenter) ? 'Tanımlı' : 'Tanımsız'}
                                        </span>
                                    </div>
                                </div>
                                <button onClick={() => setSelectedCenter(null)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200">✕</button>
                            </div>

                            <div className="mb-4">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">Kapılar</span>
                                <div className="grid grid-cols-4 gap-2">
                                    {CENTER_GATES[selectedCenter]?.map(gateNum => {
                                        const isPersonality = blueprint?.personality_activations?.some(a => a.gate === gateNum);
                                        const isDesign = blueprint?.design_activations?.some(a => a.gate === gateNum);

                                        let bgColor = 'bg-gray-100';
                                        let textColor = 'text-gray-500';

                                        if (isDesign && isPersonality) {
                                            bgColor = 'bg-gradient-to-r from-gray-700 to-rose-400';
                                            textColor = 'text-white';
                                        } else if (isPersonality) {
                                            bgColor = 'bg-gray-700';
                                            textColor = 'text-white';
                                        } else if (isDesign) {
                                            bgColor = 'bg-rose-400';
                                            textColor = 'text-white';
                                        }

                                        return (
                                            <div key={gateNum} className={`${bgColor} ${textColor} rounded-xl py-3 text-center font-black text-lg`}>
                                                {gateNum}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="flex items-center justify-center gap-4 pt-4 border-t border-gray-100">
                                <div className="flex items-center gap-1.5">
                                    <div className="w-3 h-3 rounded-full bg-gray-700" />
                                    <span className="text-[10px] font-bold text-gray-500">Kişilik</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <div className="w-3 h-3 rounded-full bg-rose-400" />
                                    <span className="text-[10px] font-bold text-gray-500">Tasarım</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <div className="w-3 h-3 rounded-full bg-gray-200" />
                                    <span className="text-[10px] font-bold text-gray-500">Pasif</span>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}

                {/* Activations */}
                <div className="bg-[#f8fafc] border border-gray-100 rounded-[3rem] p-8 shadow-sm">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-3">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                                <span className="text-[10px] font-black text-rose-500 uppercase tracking-widest">TASARIM</span>
                            </div>
                            <div className="space-y-1">
                                {blueprint?.design_activations?.slice(0, 10).map((act, i) => (
                                    <ActivationItem key={i} activation={act} side="left" />
                                ))}
                            </div>
                        </div>
                        <div className="space-y-3">
                            <div className="flex items-center gap-2 mb-4 justify-end">
                                <span className="text-[10px] font-black text-cyan-500 uppercase tracking-widest">KİŞİLİK</span>
                                <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                            </div>
                            <div className="space-y-1">
                                {blueprint?.personality_activations?.slice(0, 10).map((act, i) => (
                                    <ActivationItem key={i} activation={act} side="right" />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Channels */}
                <div className="space-y-4 pb-12">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block text-center">Tanımlı Kanallar</span>
                    <div className="flex flex-wrap gap-2 justify-center px-4">
                        {blueprint?.channels?.map((ch, i) => (
                            <span key={i} className="px-5 py-2.5 bg-white border border-gray-100 text-[#3DA7B5] text-[10px] font-black rounded-2xl shadow-sm uppercase tracking-wider">{ch}</span>
                        ))}
                    </div>
                </div>
            </motion.div>
        </div>
    );

    if (embedded) return content;

    return (
        <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
            <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
                {/* ModuleHeader */}
                {packageData && (
                    <div className="relative z-30 w-full mb-2">
                        <ModuleHeader
                            userName={packageData.user?.first_name + " " + packageData.user?.last_name}
                            subtitle={packageData.package?.name + " Modülüne Hoş Geldiniz"}
                            title={packageData.package?.name}
                            description={packageData.package?.description}
                            backgroundImage={packageData.package?.image_url}
                            onExpandedChange={setIsHeaderExpanded}
                            showToggle={true}
                            autoHeight={true}
                        />
                    </div>
                )}
                {content}
            </div>
        </div>
    );
};

export default HumanDesignMap;
